/**
 * creditSystem.js - Utilitar pentru sistemul de credite e-Tesa
 * Include logica de avertizare și document bonus când creditele sunt insuficiente
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cheia pentru sessionStorage pentru a urmări documentele bonus (session-scoped, mai sigur)
const BONUS_KEY = 'etesa_bonus_documents';

/**
 * Obține numărul de documente bonus folosite de o companie
 */
export const getBonusUsed = (companyId) => {
  try {
    const bonusData = JSON.parse(sessionStorage.getItem(BONUS_KEY) || '{}');
    return bonusData[companyId] || 0;
  } catch (error) {
    console.error('Failed to read bonus data:', error);
    return 0;
  }
};

/**
 * Marchează că s-a folosit un document bonus
 */
export const useBonusDocument = (companyId) => {
  try {
    const bonusData = JSON.parse(sessionStorage.getItem(BONUS_KEY) || '{}');
    bonusData[companyId] = (bonusData[companyId] || 0) + 1;
    sessionStorage.setItem(BONUS_KEY, JSON.stringify(bonusData));
  } catch (error) {
    console.error('Failed to save bonus document usage:', error);
  }
};

/**
 * Resetează bonusul când compania adaugă credite (apelat din PaymentsTab)
 */
export const resetBonusDocuments = (companyId) => {
  try {
    const bonusData = JSON.parse(sessionStorage.getItem(BONUS_KEY) || '{}');
    delete bonusData[companyId];
    sessionStorage.setItem(BONUS_KEY, JSON.stringify(bonusData));
  } catch (error) {
    console.error('Failed to reset bonus documents:', error);
  }
};

/**
 * Verifică dacă compania poate folosi un document bonus
 * @returns {boolean} - true dacă mai are bonus disponibil
 */
export const canUseBonusDocument = (companyId) => {
  const bonusUsed = getBonusUsed(companyId);
  return bonusUsed < 1; // Un singur document bonus permis
};

/**
 * Verifică și procesează creditele pentru o acțiune
 * @param {string} companyId - ID-ul companiei
 * @param {string} actionCode - Codul acțiunii (generate_deviz, generate_invoice, etc.)
 * @param {string} referenceId - ID referință opțional (pentru pontaj lunar)
 * @param {Function} onWarning - Callback pentru avertisment (primește mesajul)
 * @param {Function} onBlock - Callback când acțiunea e blocată complet
 * @returns {Object} - { canProceed: boolean, usedBonus: boolean, deducted: number }
 */
export const checkAndDeductCredits = async (companyId, actionCode, referenceId = null, onWarning = null, onBlock = null) => {
  try {
    // Verifică mai întâi dacă sunt credite suficiente
    const checkResponse = await axios.post(`${API}/credits/check`, null, {
      params: { company_id: companyId, action_code: actionCode, reference_id: referenceId }
    });
    
    const { price, balance, sufficient, already_paid } = checkResponse.data;
    
    // Dacă e deja plătit (ex: pontaj lunar), continuăm fără deducere
    if (already_paid) {
      return { canProceed: true, usedBonus: false, deducted: 0, alreadyPaid: true };
    }
    
    // Dacă are credite suficiente, deducem normal
    if (sufficient) {
      try {
        const deductResponse = await axios.post(`${API}/credits/deduct`, {
          company_id: companyId,
          action_code: actionCode,
          reference_id: referenceId
        });
        
        return { 
          canProceed: true, 
          usedBonus: false, 
          deducted: deductResponse.data.deducted || price,
          newBalance: deductResponse.data.new_balance
        };
      } catch (deductError) {
        console.error('Credit deduction error:', deductError);
        // Continuăm oricum dacă deducerea eșuează
        return { canProceed: true, usedBonus: false, deducted: 0 };
      }
    }
    
    // Credite insuficiente - verificăm dacă poate folosi bonus
    const canBonus = canUseBonusDocument(companyId);
    
    if (canBonus) {
      // Afișăm avertisment și permitem documentul bonus
      const warningMessage = `⚠️ ATENȚIE: Credite insuficiente!\n\nAveți ${balance} credite, dar această acțiune costă ${price} credite.\n\nAcesta este ultimul document pe care îl puteți genera gratis. Vă rugăm să alimentați contul pentru a continua să folosiți serviciile.`;
      
      if (onWarning) {
        onWarning(warningMessage);
      }
      
      // Marchează folosirea bonusului
      useBonusDocument(companyId);
      
      return { 
        canProceed: true, 
        usedBonus: true, 
        deducted: 0,
        warning: warningMessage,
        balance: balance,
        required: price
      };
    }
    
    // Nu are nici credite, nici bonus disponibil
    const blockMessage = `❌ Credite insuficiente!\n\nAveți ${balance} credite, dar această acțiune costă ${price} credite.\n\nAți folosit deja documentul bonus gratuit. Vă rugăm să alimentați contul din secțiunea "Plăți" pentru a continua.`;
    
    if (onBlock) {
      onBlock(blockMessage);
    }
    
    return { 
      canProceed: false, 
      usedBonus: false, 
      deducted: 0,
      blocked: true,
      message: blockMessage,
      balance: balance,
      required: price
    };
    
  } catch (error) {
    console.error('Credit check error:', error);
    // În caz de eroare, permitem acțiunea (fail-open pentru a nu bloca utilizatorul)
    return { canProceed: true, usedBonus: false, deducted: 0, error: true };
  }
};

/**
 * Obține soldul curent de credite
 */
export const getBalance = async (companyId) => {
  try {
    const response = await axios.get(`${API}/credits/balance`, {
      params: { company_id: companyId }
    });
    return response.data.balance || 0;
  } catch (error) {
    console.error('Failed to fetch balance:', error);
    return 0;
  }
};

/**
 * Numele acțiunilor în română pentru afișare
 */
export const ACTION_NAMES = {
  add_employee: 'Adăugare angajat',
  employee_monthly: 'Taxă lunară angajat',
  generate_deviz: 'Generare deviz',
  edit_deviz: 'Editare deviz',
  change_beneficiar_deviz: 'Schimbare beneficiar deviz',
  generate_invoice: 'Generare factură',
  edit_invoice: 'Editare factură',
  generate_contract: 'Generare contract',
  generate_foi_parcurs: 'Generare foi de parcurs',
  generate_pontaj_monthly: 'Generare pontaj lunar'
};
